// 15-step wall builder configuration.
// Each step has an id, title, question, type and options where relevant.
// showIf(state) returns false to skip the step based on prior answers.

// Completed-project photos re-used for the builder so every option shows a real
// Swift Luxury Walls installation (guaranteed on-brand, no mismatched stock).
const REAL = {
  condoMarble:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/9c1hbkq3_image.webp",
  warmStoneVeneer:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/9dzpi771_803683359_1431541392222137_4788706743518853217_n.webp",
  rgbFireplace:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/z4b6s8vu_801667425_1395832261961826_6477604479145571270_n.webp",
  twoStoryStoneVeneer:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/qcq97hhc_801368841_1072990785366922_2135911247433527635_n.webp",
  tierThreeShelves:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/eidlcvg5_799501820_950869934052320_4616448585088601457_n.jpg",
  tierFourBuiltIn:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/5qie7x5l_796581337_1644873974087786_2351055424289471612_n.jpg",
  tierOneSlats:
    "https://customer-assets-agu9un31.emergentagent.net/job_luxury-walls-preview/artifacts/nnza7v59_796354291_1780418799758870_4858807738862307886_n.jpg",
};

export const LAYOUT_OPTIONS = [
  {
    value: "tv-wall",
    label: "TV Wall",
    desc: "Clean floating TV on a feature finish.",
    img: REAL.condoMarble,
  },
  {
    value: "tv-fireplace",
    label: "TV + Fireplace",
    desc: "Linear fireplace beneath the screen.",
    img: REAL.rgbFireplace,
  },
  {
    value: "tv-fireplace-console",
    label: "TV + Fireplace + Console",
    desc: "Fireplace + floating console below.",
    img: REAL.tierFourBuiltIn,
  },
  {
    value: "full-media-wall",
    label: "Full Media Wall",
    desc: "Wall-to-wall integrated build.",
    img: REAL.twoStoryStoneVeneer,
  },
  {
    value: "bookcase-media",
    label: "Bookcase Media Wall",
    desc: "TV framed by built-in bookcases.",
    img: REAL.tierThreeShelves,
  },
  {
    value: "accent-wall",
    label: "Accent Wall",
    desc: "Feature wall — no TV integration.",
    img: "https://images.unsplash.com/photo-1675528030748-d463ab87e8d5?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
];

export const CONDITION_OPTIONS = [
  { value: "doors", label: "Doors" },
  { value: "windows", label: "Windows" },
  { value: "outlets", label: "Outlets" },
  { value: "switches", label: "Switches" },
  { value: "hvac", label: "HVAC vents" },
  { value: "cabinetry", label: "Existing cabinetry" },
  { value: "fireplace-opening", label: "Fireplace / opening" },
  { value: "clear", label: "Clear wall" },
];

export const STYLE_OPTIONS = [
  {
    value: "modern-minimal",
    label: "Modern Minimal",
    desc: "Clean lines, muted palette.",
    img: REAL.condoMarble,
  },
  {
    value: "warm-contemporary",
    label: "Warm Contemporary",
    desc: "Wood tones + soft lighting.",
    img: REAL.warmStoneVeneer,
  },
  {
    value: "luxury-statement",
    label: "Luxury Statement",
    desc: "Marble, gold trims, backlit.",
    img: REAL.twoStoryStoneVeneer,
  },
  {
    value: "traditional-built-in",
    label: "Traditional Built-In",
    desc: "Cabinetry, mantel, symmetry.",
    img: REAL.tierFourBuiltIn,
  },
];

export const TV_STATUS_OPTIONS = [
  { value: "no-tv", label: "No TV", desc: "Accent / feature only." },
  { value: "existing", label: "Existing TV", desc: "Working with what you have." },
  { value: "new", label: "New TV planned", desc: "Buying one for this build." },
];

export const TV_MOUNT_OPTIONS = [
  { value: "surface", label: "Surface-mounted", desc: "Wall-mounted, easy access." },
  { value: "recessed", label: "Recessed", desc: "Flush with the wall — cleanest look." },
];

export const MAIN_FINISH_OPTIONS = [
  {
    value: "marble-look",
    label: "Marble-look panel",
    desc: "Lightweight, dramatic veining.",
    img: "https://images.pexels.com/photos/9976125/pexels-photo-9976125.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
  },
  {
    value: "acrylic",
    label: "Acrylic panel",
    desc: "High-gloss, seamless finish.",
    img: REAL.condoMarble,
  },
  {
    value: "pu-stone",
    label: "PU stone veneer",
    desc: "Lightweight faux-stone texture.",
    img: REAL.twoStoryStoneVeneer,
  },
  {
    value: "painted",
    label: "Painted finish",
    desc: "Custom color, smooth finish.",
    img: REAL.rgbFireplace,
  },
  {
    value: "wood",
    label: "Wood finish",
    desc: "Warm, natural grain.",
    img: REAL.tierOneSlats,
  },
];

export const ACCENT_OPTIONS = [
  { value: "wood-slat", label: "Wood slats / acoustic panels" },
  { value: "stone-accent", label: "Stone-look accents" },
  { value: "painted-sections", label: "Painted sections" },
  { value: "match-main", label: "Match main finish" },
  { value: "none", label: "No accent" },
];

export const ACCENT_SIDE_OPTIONS = [
  { value: "left", label: "Left side" },
  { value: "right", label: "Right side" },
  { value: "both", label: "Both sides" },
];

export const COLOR_OPTIONS = [
  { value: "light-neutrals", label: "Light neutrals", swatch: "#E8E2D5" },
  { value: "warm-wood", label: "Warm wood", swatch: "#8B5A2B" },
  { value: "gray-charcoal", label: "Gray / charcoal", swatch: "#3F3F46" },
  { value: "black", label: "Black", swatch: "#0A0A0A" },
  { value: "bold-statement", label: "Bold statement", swatch: "#7C1D1D" },
];

export const TRIM_OPTIONS = [
  { value: "gold", label: "Gold", swatch: "#D4AF37" },
  { value: "black", label: "Black", swatch: "#0A0A0A" },
  { value: "silver", label: "Silver", swatch: "#C0C0C0" },
  { value: "matching", label: "Matching wall" },
  { value: "none", label: "No trim" },
];

export const FIREPLACE_OPTIONS = [
  { value: "none", label: "No fireplace" },
  { value: "new-electric", label: "New electric fireplace" },
  { value: "existing", label: "Incorporate existing unit" },
];

export const FIREPLACE_WIDTH_OPTIONS = [
  { value: "36", label: '36"' },
  { value: "50", label: '50"' },
  { value: "60", label: '60"' },
  { value: "72", label: '72"' },
  { value: "recommend", label: "Recommend for me" },
];

export const CONSOLE_OPTIONS = [
  { value: "none", label: "No console" },
  { value: "existing", label: "Use existing console" },
  { value: "custom-floating", label: "Custom floating console" },
  { value: "floor-cabinet", label: "Floor-standing cabinetry" },
];

export const TOWER_OPTIONS = [
  { value: "none", label: "No towers or shelves" },
  { value: "floating-shelves", label: "Floating shelves" },
  { value: "one-tower", label: "One tower" },
  { value: "two-towers", label: "Two towers" },
  { value: "bookcase", label: "Bookcase layout" },
];

export const CUBBY_PRESETS = [
  { value: "3-3", label: "3 / 3" },
  { value: "4-4", label: "4 / 4" },
  { value: "5-5", label: "5 / 5" },
  { value: "custom", label: "Custom" },
];

export const LIGHTING_OPTIONS = [
  { value: "behind-tv", label: "Behind-TV lighting" },
  { value: "shelf-cubby", label: "Shelf / cubby lights" },
  { value: "under-console", label: "Under-console lighting" },
  { value: "accent", label: "Accent lighting" },
  { value: "none", label: "No lighting" },
];

export const LIGHTING_COLOR_OPTIONS = [
  { value: "warm-white", label: "Warm white" },
  { value: "color-changing", label: "Color-changing" },
];

export const EQUIPMENT_OPTIONS = [
  { value: "soundbar", label: "Soundbar" },
  { value: "speakers", label: "Surround speakers" },
  { value: "receiver", label: "Receiver / AV rack" },
  { value: "subwoofer", label: "Subwoofer" },
  { value: "other", label: "Other equipment" },
];

export const BUDGET_OPTIONS = [
  { value: "under-3500", label: "Under $3,500" },
  { value: "3500-5999", label: "$3,500 – $5,999" },
  { value: "6000-8999", label: "$6,000 – $8,999" },
  { value: "9000-11999", label: "$9,000 – $11,999" },
  { value: "12000-plus", label: "$12,000+" },
  { value: "need-guidance", label: "Need guidance" },
];

export const TIMING_OPTIONS = [
  { value: "asap", label: "ASAP" },
  { value: "1-3-months", label: "1 – 3 months" },
  { value: "3-6-months", label: "3 – 6 months" },
  { value: "exploring", label: "Just exploring" },
];

export const CONTACT_METHODS = [
  { value: "phone", label: "Phone" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
];

// ---- Step definitions -------------------------------------------------
// Each: id, title, subtitle, key(s) in state, and skip logic
// Order goals (post-Phase-2): budget lives near the top so we qualify scope
// early, and fireplace/console/towers are combined into a single "extras" step
// to cut question count. Steps hide automatically when they don't apply
// (e.g. accent-wall layout skips TV / extras).
export const STEPS = [
  { id: "location", title: "Project location", subtitle: "Where will we install your wall?" },
  { id: "layout", title: "Starting layout", subtitle: "What would you like to build?" },
  { id: "measurements", title: "Wall measurements", subtitle: "How much space do you have?" },
  { id: "budget", title: "Budget & timing", subtitle: "Helps us match scope to plan — right up front." },
  { id: "conditions", title: "Existing conditions", subtitle: "What do we need to work around?" },
  { id: "style", title: "Overall style", subtitle: "Which look fits your home?" },
  {
    id: "tv",
    title: "TV setup",
    subtitle: "How should your TV fit into the design?",
    showIf: (s) => s.layout !== "accent-wall",
  },
  {
    id: "mainFinish",
    title: "Main wall finish",
    subtitle: "What finish behind the TV or across the wall?",
  },
  {
    id: "accent",
    title: "Accent materials",
    subtitle: "Add contrast on the sides.",
  },
  { id: "colors", title: "Colors & trim", subtitle: "Set the palette." },
  {
    id: "extras",
    title: "Fireplace, console & shelving",
    subtitle: "Everything sitting on or beside the wall.",
    showIf: (s) => s.layout !== "accent-wall",
  },
  { id: "lighting", title: "Lighting & equipment", subtitle: "Set the mood, plan the gear." },
  { id: "review", title: "Review & submit", subtitle: "Confirm your selections." },
];
